// Custom CSS styles for menu items (foods.item_css / item_name_css / item_description_css, used while
// foods.custom_css_styles is on). The backend only stores allowlisted declarations (MenuCss in
// menutranslator); this applies the same allowlist again before anything reaches a style binding.

// Keep in sync with MenuCss::ALLOWED_PROPERTIES and friends. Left out on purpose: anything that can move an
// item out of its place or over the page (position, offsets, z-index, transform, float), hide or rewrite
// content (display, visibility, content, pointer-events), or load images (background-image, cursor, ...).
const ALLOWED_PROPERTIES = [
  'color', 'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant', 'line-height', 'letter-spacing', 'word-spacing',
  'text-transform', 'text-align', 'text-indent', 'white-space', 'text-shadow',
  'text-decoration', 'text-decoration-line', 'text-decoration-color', 'text-decoration-style',
  'background', 'background-color', 'opacity',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left', 'border-width', 'border-style', 'border-color', 'border-radius',
  'box-shadow', 'outline', 'max-width', 'min-height'
]
// The only functions a value may use: colours and gradients (no url, image-set, expression, var, attr, calc...).
const ALLOWED_FUNCTIONS = ['rgb', 'rgba', 'hsl', 'hsla', 'linear-gradient', 'radial-gradient']
const NO_NEGATIVE_LENGTHS = ['margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'text-indent']
const SHADOW_PROPERTIES = ['box-shadow', 'text-shadow', 'outline']
const MAX_LENGTH_PX = 600
const MAX_SHADOW_PX = 50
const MIN_OPACITY = 0.5
const SAFE_CHARACTERS = /^[a-zA-Z0-9#%.,\-'" ()/]+$/
const UNSAFE_WORDS = /expression|javascript|url|import/i

function balancedParentheses(value) {
  let depth = 0
  for (const char of value) {
    if (char === '(') depth++
    if (char === ')') depth--
    if (depth < 0 || depth > 2) return false
  }
  return depth === 0
}

function safeValue(value) {
  if (!value || value.length > 300 || !SAFE_CHARACTERS.test(value) || UNSAFE_WORDS.test(value)) {
    return false
  }
  const functions = [...value.matchAll(/([a-zA-Z-]+)\s*\(/g)].map(match => match[1].toLowerCase())
  if (!functions.every(name => ALLOWED_FUNCTIONS.includes(name)) || (value.match(/\(/g) || []).length !== functions.length) {
    return false
  }
  return balancedParentheses(value)
}

// Size limits: no negative margins or indents, capped lengths (tighter for shadows), no near-invisible opacity.
function safeForProperty(property, value) {
  if (property === 'opacity') {
    return /^(0?\.\d+|1(\.0+)?|0|1)$/.test(value) && parseFloat(value) >= MIN_OPACITY
  }
  if (NO_NEGATIVE_LENGTHS.includes(property) && /(^|[^\w])-\d/.test(value)) {
    return false
  }
  const limit = SHADOW_PROPERTIES.includes(property) ? MAX_SHADOW_PX : MAX_LENGTH_PX
  // Lengths always carry a unit (unitless numbers are weights, line heights or colour channels).
  // (No lookbehind here: Safari only supports it from 16.4, and a syntax error would break the whole page.)
  return [...value.matchAll(/(^|[^#\w.])(-?\d*\.?\d+)(px|em|rem|vh|vw|vmin|vmax|%)(?![a-z])/gi)].every(match => {
    const amount = Math.abs(parseFloat(match[2]))
    const unit = match[3].toLowerCase()
    if (unit === 'px') return amount <= limit
    if (unit === 'em' || unit === 'rem') return amount <= limit / 16
    if (unit === '%') return amount <= 100
    return false // viewport units can stretch an item across the screen
  })
}

// Fonts already loaded by App.vue, plus generic families that never need loading.
const PRELOADED_FONTS = [
  'Montserrat', 'Roboto', 'Merriweather', 'Ubuntu', 'Inconsolata', 'Overpass', 'Source Serif Pro', 'Open Sans',
  'Economica', 'Permanent Marker', 'Sacramento', 'Josefin Slab', 'Zilla Slab', 'Cabin', 'Inter',
  'serif', 'sans-serif', 'monospace', 'cursive'
]
const requestedFonts = new Set()

const camelCase = property => property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())

export { ALLOWED_PROPERTIES }

// True when "property: value" passes the allowlist (the same rules as MenuCss.sanitize on the server).
export function isAllowedDeclaration(property, value) {
  property = (property || '').trim().toLowerCase()
  value = (value || '').trim()
  return ALLOWED_PROPERTIES.includes(property) && safeValue(value) && safeForProperty(property, value)
}

// "color: #111; font-size: 16px;" => [{ property: 'color', value: '#111' }, ...], in order, keeping only
// allowlisted declarations.
export function declarations(css) {
  return (css || '').split(';').map(declaration => {
    const index = declaration.indexOf(':')
    return index === -1 ? null : { property: declaration.slice(0, index).trim().toLowerCase(), value: declaration.slice(index + 1).trim() }
  }).filter(declaration => declaration && isAllowedDeclaration(declaration.property, declaration.value))
}

// [{ property, value }, ...] => "property: value; ..." (allowed declarations only), or null when empty.
export function toCss(rows) {
  const css = rows.filter(row => isAllowedDeclaration(row.property, row.value)).map(row => `${row.property.trim().toLowerCase()}: ${row.value.trim()}`)
  return css.length > 0 ? css.join('; ') + ';' : null
}

// "color: #111; font-size: 16px;" => { color: '#111', fontSize: '16px' }, dropping anything not allowlisted.
export function parseCss(css) {
  const style = {}
  declarations(css).forEach(({ property, value }) => {
    style[camelCase(property)] = value
  })
  return style
}

// Loads (once per page) the Google Fonts named in the given style objects' font-family values.
export function loadFonts(...styles) {
  styles.forEach(style => {
    if (!style || !style.fontFamily) {
      return
    }
    style.fontFamily.split(',').map(name => name.trim().replace(/^['"]|['"]$/g, '')).forEach(name => {
      if (!name || PRELOADED_FONTS.includes(name) || requestedFonts.has(name) || !/^[A-Za-z0-9 ]+$/.test(name)) {
        return
      }
      requestedFonts.add(name)
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}:ital,wght@0,300;0,400;0,600;0,700;0,800;1,400;1,700&display=swap`
      document.head.appendChild(link)
    })
  })
}

// The three style objects for a food, empty when its custom styles are off.
export function foodCustomStyles(food) {
  if (!food || food.custom_css_styles !== true) {
    return { item: {}, name: {}, description: {} }
  }
  const styles = { item: parseCss(food.item_css), name: parseCss(food.item_name_css), description: parseCss(food.item_description_css) }
  loadFonts(styles.name, styles.description)
  return styles
}
